import { StreamableFile } from "@nestjs/common";
import { initContract } from "@ts-rest/core";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import {
    ChartOfAccountsTemplateAccountsResponseSchema,
    ChartOfAccountsTemplateListQuerySchema,
    ChartOfAccountsTemplateListResponseSchema,
    ChartOfAccountsTemplatePathParamsSchema,
    ChartOfAccountsTemplateIdPathParamsSchema,
    ChartOfAccountsTemplateCreateSchema,
    ChartOfAccountsTemplateUpdateSchema,
} from "../schemas/chart-of-accounts-template.schema";
import {
    ChartOfAccountsTemplateQuickbooksImportBodySchema,
    ChartOfAccountsTemplateUploadResponseSchema,
} from "../schemas/chart-of-accounts-template-upload.schema";
import { ChartOfAccountsTemplateAccountsBulkSaveSchema } from "../schemas/chart-of-accounts-template-account.schema";
import {
    ChartOfAccountsTemplateApplyBodySchema,
    ChartOfAccountsTemplateApplyResponseSchema,
    ChartOfAccountsTemplatePreviewResponseSchema,
    ChartOfAccountsTemplateAppliedEntitiesResponseSchema,
    ChartOfAccountsTemplateActivityQuerySchema,
} from "../schemas/chart-of-accounts-template-apply.schema";
import { ChartOfAccountsTemplateSchema } from "../types/chartOfAccountsTemplate";
import { HistoryResponseSchema } from "./auditLog.contract";

export {
    ChartOfAccountsTemplateCreateSchema,
    ChartOfAccountsTemplateUpdateSchema,
};

export type {
    ChartOfAccountsTemplateCreate,
    ChartOfAccountsTemplateUpdate,
} from "../schemas/chart-of-accounts-template.schema";

const c = initContract();

export const chartOfAccountsTemplateContract = c.router(
    {
        listChartOfAccountsTemplates: {
            method: "GET",
            path: "/organizations/:organizationId",
            pathParams: ChartOfAccountsTemplatePathParamsSchema,
            query: ChartOfAccountsTemplateListQuerySchema,
            responses: {
                200: ChartOfAccountsTemplateListResponseSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        listAppliedEntitiesForChartOfAccountsTemplate: {
            method: "GET",
            path: "/organizations/:organizationId/:chartOfAccountsTemplateId/applied-entities",
            pathParams: ChartOfAccountsTemplatePathParamsSchema.merge(
                ChartOfAccountsTemplateIdPathParamsSchema,
            ),
            responses: {
                200: ChartOfAccountsTemplateAppliedEntitiesResponseSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        listActivityForChartOfAccountsTemplate: {
            method: "GET",
            path: "/organizations/:organizationId/:chartOfAccountsTemplateId/activity",
            pathParams: ChartOfAccountsTemplatePathParamsSchema.merge(
                ChartOfAccountsTemplateIdPathParamsSchema,
            ),
            query: ChartOfAccountsTemplateActivityQuerySchema,
            responses: {
                200: HistoryResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        parseChartOfAccountsTemplateFromQuickbooks: {
            method: "POST",
            path: "/organizations/:organizationId/parse-quickbooks",
            pathParams: ChartOfAccountsTemplatePathParamsSchema,
            body: ChartOfAccountsTemplateQuickbooksImportBodySchema,
            responses: {
                200: ChartOfAccountsTemplateUploadResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        createChartOfAccountsTemplate: {
            method: "POST",
            path: "/organizations/:organizationId",
            pathParams: ChartOfAccountsTemplatePathParamsSchema,
            body: ChartOfAccountsTemplateCreateSchema,
            responses: {
                201: ChartOfAccountsTemplateSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        updateChartOfAccountsTemplate: {
            method: "PATCH",
            path: "/organizations/:organizationId/:chartOfAccountsTemplateId",
            pathParams: ChartOfAccountsTemplatePathParamsSchema.merge(
                ChartOfAccountsTemplateIdPathParamsSchema,
            ),
            body: ChartOfAccountsTemplateUpdateSchema,
            responses: {
                200: ChartOfAccountsTemplateSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        deleteChartOfAccountsTemplate: {
            method: "DELETE",
            path: "/organizations/:organizationId/:chartOfAccountsTemplateId",
            pathParams: ChartOfAccountsTemplatePathParamsSchema.merge(
                ChartOfAccountsTemplateIdPathParamsSchema,
            ),
            responses: {
                200: c.noBody(),
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        archiveChartOfAccountsTemplate: {
            method: "POST",
            path: "/organizations/:organizationId/:chartOfAccountsTemplateId/archive",
            pathParams: ChartOfAccountsTemplatePathParamsSchema.merge(
                ChartOfAccountsTemplateIdPathParamsSchema,
            ),
            body: c.noBody(),
            responses: {
                200: ChartOfAccountsTemplateSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        unarchiveChartOfAccountsTemplate: {
            method: "POST",
            path: "/organizations/:organizationId/:chartOfAccountsTemplateId/unarchive",
            pathParams: ChartOfAccountsTemplatePathParamsSchema.merge(
                ChartOfAccountsTemplateIdPathParamsSchema,
            ),
            body: c.noBody(),
            responses: {
                200: ChartOfAccountsTemplateSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getChartOfAccountsTemplateAccounts: {
            method: "GET",
            path: "/organizations/:organizationId/:chartOfAccountsTemplateId/accounts",
            pathParams: ChartOfAccountsTemplatePathParamsSchema.merge(
                ChartOfAccountsTemplateIdPathParamsSchema,
            ),
            responses: {
                200: ChartOfAccountsTemplateAccountsResponseSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        exportChartOfAccountsTemplateCsv: {
            method: "GET",
            path: "/organizations/:organizationId/:chartOfAccountsTemplateId/csv",
            pathParams: ChartOfAccountsTemplatePathParamsSchema.merge(
                ChartOfAccountsTemplateIdPathParamsSchema,
            ),
            responses: {
                200: c.type<StreamableFile>(),
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        saveChartOfAccountsTemplateAccounts: {
            method: "PUT",
            path: "/organizations/:organizationId/:chartOfAccountsTemplateId/accounts",
            pathParams: ChartOfAccountsTemplatePathParamsSchema.merge(
                ChartOfAccountsTemplateIdPathParamsSchema,
            ),
            body: ChartOfAccountsTemplateAccountsBulkSaveSchema,
            responses: {
                200: ChartOfAccountsTemplateAccountsResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        previewApplyChartOfAccountsTemplate: {
            method: "POST",
            path: "/organizations/:organizationId/:chartOfAccountsTemplateId/preview-apply",
            pathParams: ChartOfAccountsTemplatePathParamsSchema.merge(
                ChartOfAccountsTemplateIdPathParamsSchema,
            ),
            body: ChartOfAccountsTemplateApplyBodySchema,
            responses: {
                200: ChartOfAccountsTemplatePreviewResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        applyChartOfAccountsTemplate: {
            method: "POST",
            path: "/organizations/:organizationId/:chartOfAccountsTemplateId/apply",
            pathParams: ChartOfAccountsTemplatePathParamsSchema.merge(
                ChartOfAccountsTemplateIdPathParamsSchema,
            ),
            body: ChartOfAccountsTemplateApplyBodySchema,
            responses: {
                201: ChartOfAccountsTemplateApplyResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
    },
    {
        pathPrefix: "/chart-of-accounts-templates",
    },
);
